import {Ban, Building2, Eye, KeyRound, Lock, Search, ShieldCheck, User, UserCheck, UserPlus, Users, X} from 'lucide-react';
import {Alert, Autocomplete, CircularProgress, Dialog, DialogContent, TextField} from '@mui/material';
import {useMemo, useState} from 'react';
import {PortalShell} from '../../shared/ui';
import {usePortalUsers} from './usePortalUsers';
import {initialsOf, normalizeLicenseLabel, normalizeReferenceId, normalizeRoleLabel, PAPEL_NO_VINCULO_OPTIONS, perfisDaLicenca} from './usersUtils';

const STATUS_UI = {
    Ativo: {label: 'Ativo', className: 'bf-pill bf-pill--success'},
    Inativo: {label: 'Desativado', className: 'bf-pill bf-pill--muted'},
    Bloqueado: {label: 'Bloqueado', className: 'bf-pill bf-pill--danger'},
};

// Situação do vínculo (BBF-67), com as mesmas cores da situação da pessoa.
const SITUACAO_UI = {ATIVO: STATUS_UI.Ativo, DESATIVADO: STATUS_UI.Inativo, BLOQUEADO: STATUS_UI.Bloqueado};

const PAPEL_UI = {
    ADMINISTRADOR: {label: 'Administrador', className: 'bf-pill bf-pill--primary', icon: ShieldCheck, desc: 'Gerencia a assinatura e os usuários dela.'},
    USUARIO: {label: 'Usuário', className: 'bf-pill bf-pill--muted', icon: User, desc: 'Usa os sistemas com os perfis que você der.'},
};

const FILTROS = [
    {value: 'Todos', label: 'Todos', count: (stats) => stats.total},
    {value: 'Ativo', label: 'Ativos', count: (stats) => stats.active},
    {value: 'Inativo', label: 'Desativados', count: (stats) => stats.inactive},
    {value: 'Bloqueado', label: 'Bloqueados', count: (stats) => stats.blocked},
];

const MAX_PERFIS_NA_LISTA = 2;

const autocompleteSx = {'& .MuiOutlinedInput-root': {borderRadius: '12px', background: '#fff'}};
const dialogPaperProps = {sx: {borderRadius: '16px'}};

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
}

function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
}

function formatPhone(raw) {
    const numero = raw?.mobilePhoneNumber || raw?.phoneNumber;
    if (!numero) return '-';
    const ddi = raw?.ddiPhoneNumber || raw?.ddi;
    return ddi ? `+${ddi} ${numero}` : numero;
}

function Field({id, label, required, hint, ...inputProps}) {
    return (
        <div className="bf-field">
            <label className="bf-label" htmlFor={id}>
                {label}{required ? <span className="bf-required"> *</span> : null}
            </label>
            <input id={id} className="bf-input" required={required} {...inputProps} />
            {hint ? <span className="bf-hint">{hint}</span> : null}
        </div>
    );
}

function PapelPill({papel, sufixo}) {
    const ui = PAPEL_UI[papel] || PAPEL_UI.USUARIO;
    return <span className={ui.className}>{ui.label}{sufixo ? ` · ${sufixo}` : ''}</span>;
}

/** Escolha do papel numa licença. Administrador aparece travado para quem não pode concedê-lo. */
function EscolhaDoPapel({licenseId, permitidos, value, onChange}) {
    return (
        <div className="bf-choice-group" role="radiogroup" aria-label="Papel na assinatura">
            {PAPEL_NO_VINCULO_OPTIONS.map((option) => {
                const ui = PAPEL_UI[option.value];
                const Icon = ui.icon;
                const disabled = !permitidos.includes(option.value);
                const selected = value === option.value;
                const className = ['bf-choice', selected ? 'bf-choice--selected' : '', disabled ? 'bf-choice--disabled' : '']
                    .filter(Boolean)
                    .join(' ');
                return (
                    <label key={option.value} className={className}>
                        <input
                            type="radio"
                            name={`papel-${licenseId}`}
                            value={option.value}
                            checked={selected}
                            disabled={disabled}
                            onChange={() => onChange(option.value)}
                        />
                        <span className="bf-choice__icon">{disabled ? <Lock size={18} /> : <Icon size={18} />}</span>
                        <span>
                            <span className="bf-choice__title">{option.label}</span>
                            <span className="bf-choice__desc" style={{display: 'block'}}>
                                {disabled ? 'Só um Administrador da assinatura concede este papel.' : ui.desc}
                            </span>
                        </span>
                    </label>
                );
            })}
        </div>
    );
}

function Dado({label, children}) {
    return (
        <div>
            <div className="bf-meta-label">{label}</div>
            <div className="bf-meta-value">{children}</div>
        </div>
    );
}

/** Detalhes de uma pessoa: dados dela e, por assinatura, papel, situação, validade e perfis. */
function DetalhesDoUsuario({user, licencaPorId, onClose}) {
    const status = STATUS_UI[user.status] || STATUS_UI.Ativo;
    const licencasDosVinculos = new Set(user.vinculos.map((vinculo) => vinculo.licenseId));
    const perfisSemVinculo = user.perfis.filter((perfil) => !licencasDosVinculos.has(perfil.licenseId));
    return (
        <>
            <div className="bf-dialog__head">
                <div className="bf-person">
                    <span className="bf-avatar bf-avatar--lg bf-avatar--soft">{initialsOf(user.name)}</span>
                    <div style={{minWidth: 0}}>
                        <h2 className="bf-dialog__title">{user.name}</h2>
                        <div className="bf-person__email">{user.email}</div>
                        <div className="bf-stack-1" style={{marginTop: 8}}>
                            <span className={status.className}>{status.label}</span>
                            {user.raw?.mustRenewPassword ? <span className="bf-pill bf-pill--warning">Troca de senha pendente</span> : null}
                        </div>
                    </div>
                </div>
                <button type="button" className="bf-icon-button" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
            </div>

            <DialogContent dividers sx={{p: 3}}>
                <section className="bf-form-section">
                    <h3 className="bf-form-section__title"><User size={18} />Dados da pessoa</h3>
                    <div className="bf-modal-grid">
                        <Dado label="E-mail"><span style={{overflowWrap: 'anywhere'}}>{user.email}</span></Dado>
                        <Dado label="Telefone">{formatPhone(user.raw)}</Dado>
                        <Dado label="Cadastrado em">{formatDateTime(user.raw?.createdAt)}</Dado>
                        <Dado label="Atualizado em">{formatDateTime(user.raw?.updatedAt)}</Dado>
                        {user.raw?.expirationDate ? <Dado label="Acesso válido até">{formatDate(user.raw.expirationDate)}</Dado> : null}
                    </div>
                </section>

                <section className="bf-form-section">
                    <h3 className="bf-form-section__title"><Building2 size={18} />Acesso por assinatura</h3>
                    {user.vinculos.length === 0 ? <div className="bf-muted" style={{fontSize: 14}}>Sem vínculo com assinatura.</div> : null}
                    {user.vinculos.map((vinculo) => {
                        const situacao = SITUACAO_UI[vinculo.situacao] || STATUS_UI.Ativo;
                        const perfis = user.perfis.filter((perfil) => perfil.licenseId === vinculo.licenseId);
                        return (
                            <div key={vinculo.licenseId} className="bf-license-card">
                                <div className="bf-row-between">
                                    <div className="bf-license-card__name"><Building2 size={16} />{licencaPorId.get(vinculo.licenseId) || 'Sua assinatura'}</div>
                                    <span className={situacao.className}>{situacao.label}</span>
                                </div>
                                <div className="bf-modal-grid">
                                    <Dado label="Papel"><PapelPill papel={vinculo.papel} /></Dado>
                                    <Dado label="Validade">{vinculo.validoAte ? `até ${formatDate(vinculo.validoAte)}` : 'Sem prazo'}</Dado>
                                </div>
                                <Dado label="Perfis nesta assinatura">
                                    {perfis.length === 0 ? <span className="bf-muted" style={{fontWeight: 400}}>Nenhum perfil nesta assinatura.</span> : (
                                        <div className="bf-stack-1">
                                            {perfis.map((perfil) => <span key={perfil.id || perfil.nome} className="bf-pill bf-pill--primary">{perfil.nome}</span>)}
                                        </div>
                                    )}
                                </Dado>
                            </div>
                        );
                    })}
                    {perfisSemVinculo.length > 0 ? (
                        <Dado label="Outros perfis">
                            <div className="bf-stack-1">
                                {perfisSemVinculo.map((perfil) => <span key={perfil.id || perfil.nome} className="bf-pill bf-pill--muted">{perfil.nome}</span>)}
                            </div>
                        </Dado>
                    ) : null}
                </section>
            </DialogContent>

            <div className="bf-dialog__foot">
                <button type="button" className="bf-button bf-button--secondary" onClick={onClose}>Fechar</button>
            </div>
        </>
    );
}

export default function UsuariosPage() {
    const data = usePortalUsers();
    const [selectedUser, setSelectedUser] = useState(null);

    const licencaPorId = useMemo(() => new Map(
        (data.references.licencas || []).map((licenca) => [normalizeReferenceId(licenca), normalizeLicenseLabel(licenca)]),
    ), [data.references.licencas]);

    const kpis = [
        {label: 'Total de usuários', value: data.stats.total, icon: Users, color: 'var(--bf-primary)'},
        {label: 'Ativos', value: data.stats.active, icon: UserCheck, color: 'var(--bf-success)'},
        {label: 'Bloqueados', value: data.stats.blocked, icon: Ban, color: 'var(--bf-danger)'},
    ];

    const licencasDisponiveis = data.references.licencas || [];
    const licencasEscolhidas = data.form.licenses || [];
    const licencaFixa = licencasDisponiveis.length === 1 && licencasEscolhidas.length === 1;
    const fecharCadastro = () => data.setDialogOpen(false);
    const setCampo = (campo) => (event) => data.setForm((current) => ({...current, [campo]: event.target.value}));
    const buscando = Boolean(data.search.trim());

    return (
        <PortalShell>
            <div className="bf-page">
                <div className="bf-row-between" style={{flexWrap: 'wrap'}}>
                    <div>
                        <h1 className="bf-page__title">Usuários</h1>
                        <p className="bf-page__subtitle" style={{margin: 0}}>Quem tem acesso à sua assinatura e o que cada pessoa pode fazer.</p>
                    </div>
                    <button type="button" className="bf-button bf-button--primary" onClick={data.openCreate}>
                        <UserPlus size={18} />Novo usuário
                    </button>
                </div>

                {data.error ? <Alert severity="warning">{data.error}</Alert> : null}

                <div className="bf-grid-3">
                    {kpis.map(({label, value, icon: Icon, color}) => (
                        <div className="bf-card" key={label}>
                            <div className="bf-card__body">
                                <div className="bf-row" style={{gap: 12, marginBottom: 12}}>
                                    <div style={{color}}><Icon size={20} /></div>
                                    <span className="bf-muted" style={{fontSize: 14}}>{label}</span>
                                </div>
                                <div className="bf-kpi-value">{value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bf-card">
                    <div className="bf-card__body">
                        <div className="bf-toolbar">
                            <div className="bf-search">
                                <Search size={16} className="bf-search__icon" />
                                <input
                                    className="bf-input"
                                    type="search"
                                    value={data.search}
                                    onChange={(event) => data.setSearch(event.target.value)}
                                    placeholder="Buscar por nome de usuário ou e-mail"
                                    aria-label="Buscar usuário"
                                />
                            </div>
                            <div className="bf-filter-row" role="group" aria-label="Filtrar por situação">
                                {FILTROS.map((filtro) => (
                                    <button
                                        key={filtro.value}
                                        type="button"
                                        className={`bf-button bf-button--sm ${data.status === filtro.value ? 'bf-button--primary' : 'bf-button--secondary'}`}
                                        aria-pressed={data.status === filtro.value}
                                        onClick={() => data.setStatus(filtro.value)}
                                    >
                                        {filtro.label} ({filtro.count(data.stats)})
                                    </button>
                                ))}
                            </div>
                        </div>

                        {data.loading ? (
                            <div className="bf-empty"><CircularProgress /></div>
                        ) : data.rows.length === 0 ? (
                            <div className="bf-empty">
                                <Users size={40} style={{marginBottom: 12}} />
                                {data.stats.total === 0 && !buscando ? (
                                    <>
                                        <div style={{fontWeight: 600, fontSize: 18, marginBottom: 8, color: 'var(--bf-foreground)'}}>Nenhum usuário ainda</div>
                                        <div style={{marginBottom: 16}}>Cadastre a primeira pessoa da sua equipe.</div>
                                        <button type="button" className="bf-button bf-button--primary" onClick={data.openCreate}><UserPlus size={18} />Novo usuário</button>
                                    </>
                                ) : (
                                    <>
                                        <div style={{fontWeight: 600, fontSize: 18, marginBottom: 8, color: 'var(--bf-foreground)'}}>Nenhum usuário encontrado</div>
                                        <div>Tente outro nome, e-mail ou situação.</div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="bf-table-wrap">
                                <table className="bf-table">
                                    <thead>
                                        <tr>
                                            <th>Usuário</th>
                                            <th>Papel</th>
                                            <th>Perfis</th>
                                            <th>Situação</th>
                                            <th style={{textAlign: 'right'}}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rows.map((user) => {
                                            const status = STATUS_UI[user.status] || STATUS_UI.Ativo;
                                            const variosVinculos = user.vinculos.length > 1;
                                            const validade = user.vinculos.length === 1 ? user.vinculos[0].validoAte : null;
                                            const perfisVisiveis = user.perfis.slice(0, MAX_PERFIS_NA_LISTA);
                                            const perfisOcultos = user.perfis.length - perfisVisiveis.length;
                                            return (
                                                <tr key={user.id}>
                                                    <td>
                                                        <div className="bf-person">
                                                            <span className="bf-avatar bf-avatar--soft">{initialsOf(user.name)}</span>
                                                            <div style={{minWidth: 0}}>
                                                                <div className="bf-person__name">{user.name}</div>
                                                                <div className="bf-person__email">{user.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {user.vinculos.length === 0 ? <span className="bf-muted">-</span> : (
                                                            <div className="bf-stack-1">
                                                                {user.vinculos.map((vinculo) => (
                                                                    <PapelPill
                                                                        key={vinculo.licenseId}
                                                                        papel={vinculo.papel}
                                                                        sufixo={variosVinculos ? licencaPorId.get(vinculo.licenseId) : ''}
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.perfis.length === 0 ? <span className="bf-muted">Nenhum</span> : (
                                                            <div className="bf-stack-1">
                                                                {perfisVisiveis.map((perfil) => <span key={perfil.id || perfil.nome} className="bf-pill bf-pill--muted">{perfil.nome}</span>)}
                                                                {perfisOcultos > 0 ? <span className="bf-pill bf-pill--muted" title={user.perfis.slice(MAX_PERFIS_NA_LISTA).map((perfil) => perfil.nome).join(', ')}>+{perfisOcultos}</span> : null}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={status.className}>{status.label}</span>
                                                        {validade ? <div className="bf-hint" style={{marginTop: 4}}>até {formatDate(validade)}</div> : null}
                                                    </td>
                                                    <td style={{textAlign: 'right'}}>
                                                        <button type="button" className="bf-button bf-button--ghost bf-button--sm" onClick={() => setSelectedUser(user)}>
                                                            <Eye size={16} />Ver detalhes
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Dialog open={Boolean(selectedUser)} onClose={() => setSelectedUser(null)} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
                {selectedUser ? (
                    <DetalhesDoUsuario user={selectedUser} licencaPorId={licencaPorId} onClose={() => setSelectedUser(null)} />
                ) : null}
            </Dialog>

            <Dialog open={data.dialogOpen} onClose={data.saving ? undefined : fecharCadastro} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
                <form
                    noValidate
                    style={{display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto'}}
                    onSubmit={(event) => {
                        event.preventDefault();
                        data.submitCreate();
                    }}
                >
                    <div className="bf-dialog__head">
                        <div>
                            <h2 className="bf-dialog__title">Novo usuário</h2>
                            <p className="bf-muted" style={{margin: '4px 0 0', fontSize: 14}}>A pessoa entra na sua assinatura com o papel e os perfis que você escolher.</p>
                        </div>
                        <button type="button" className="bf-icon-button" onClick={fecharCadastro} disabled={data.saving} aria-label="Fechar"><X size={18} /></button>
                    </div>

                    <DialogContent dividers sx={{p: 3}}>
                        <section className="bf-form-section">
                            <h3 className="bf-form-section__title"><User size={18} />Dados da pessoa</h3>
                            <div className="bf-form-grid">
                                <Field id="novo-usuario-nome" label="Nome de usuário" required autoComplete="off" value={data.form.username} onChange={setCampo('username')} />
                                <Field id="novo-usuario-email" label="E-mail" type="email" required autoComplete="off" value={data.form.email} onChange={setCampo('email')} />
                            </div>
                            <div className="bf-form-grid bf-form-grid--phone">
                                <Field id="novo-usuario-ddi" label="DDI" inputMode="numeric" value={data.form.ddiPhoneNumber} onChange={setCampo('ddiPhoneNumber')} />
                                <Field id="novo-usuario-telefone" label="Telefone" inputMode="tel" placeholder="DDD + número" value={data.form.mobilePhoneNumber} onChange={setCampo('mobilePhoneNumber')} />
                            </div>
                        </section>

                        <section className="bf-form-section">
                            <h3 className="bf-form-section__title"><KeyRound size={18} />Senha</h3>
                            <div className="bf-form-grid">
                                <Field id="novo-usuario-senha" label="Senha" type="password" required autoComplete="new-password" value={data.form.password} onChange={setCampo('password')} />
                                <Field id="novo-usuario-confirmacao" label="Confirmar senha" type="password" required autoComplete="new-password" value={data.form.confirmPassword} onChange={setCampo('confirmPassword')} />
                            </div>
                        </section>

                        <section className="bf-form-section">
                            <h3 className="bf-form-section__title"><Building2 size={18} />Acesso na assinatura</h3>
                            {licencaFixa ? null : licencasDisponiveis.length === 0 ? (
                                <div className="bf-muted" style={{fontSize: 14}}>Não encontramos assinaturas para escolher. A pessoa entra na sua assinatura como Usuário.</div>
                            ) : (
                                <div className="bf-field">
                                    <span className="bf-label">Assinaturas</span>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={licencasDisponiveis}
                                        value={licencasEscolhidas}
                                        onChange={(_, value) => data.setForm((current) => ({...current, licenses: value}))}
                                        isOptionEqualToValue={(option, value) => normalizeReferenceId(option) === normalizeReferenceId(value)}
                                        getOptionLabel={normalizeLicenseLabel}
                                        sx={autocompleteSx}
                                        renderInput={(params) => <TextField {...params} placeholder="Escolha as assinaturas" inputProps={{...params.inputProps, 'aria-label': 'Assinaturas'}} />}
                                    />
                                </div>
                            )}
                            {licencasEscolhidas.length > 0 ? (
                                <p className="bf-muted" style={{margin: 0, fontSize: 13}}>Em cada assinatura, escolha o papel e os perfis. Os perfis são da assinatura e só valem nela.</p>
                            ) : null}
                            {licencasEscolhidas.map((license) => {
                                const licenseId = normalizeReferenceId(license);
                                const nomeDaLicenca = normalizeLicenseLabel(license);
                                const permitidos = data.papelOptions(licenseId).map((option) => option.value);
                                const papel = permitidos.includes(data.form.papeis?.[licenseId]) ? data.form.papeis[licenseId] : 'USUARIO';
                                const perfisDisponiveis = perfisDaLicenca(data.references.roles, licenseId);
                                return (
                                    <div key={licenseId} className="bf-license-card">
                                        <div className="bf-license-card__name"><Building2 size={16} />{nomeDaLicenca}</div>
                                        <div className="bf-field">
                                            <span className="bf-label">Papel</span>
                                            <EscolhaDoPapel
                                                licenseId={licenseId}
                                                permitidos={permitidos}
                                                value={papel}
                                                onChange={(value) => data.setForm((current) => ({
                                                    ...current,
                                                    papeis: {...(current.papeis || {}), [licenseId]: value},
                                                }))}
                                            />
                                        </div>
                                        <div className="bf-field">
                                            <span className="bf-label">Perfis nesta assinatura</span>
                                            {perfisDisponiveis.length === 0 ? (
                                                <span className="bf-muted" style={{fontSize: 13}}>Esta assinatura ainda não tem perfis cadastrados.</span>
                                            ) : (
                                                <Autocomplete
                                                    multiple
                                                    size="small"
                                                    options={perfisDisponiveis}
                                                    value={data.form.perfis?.[licenseId] || []}
                                                    onChange={(_, value) => data.setForm((current) => ({
                                                        ...current,
                                                        perfis: {...(current.perfis || {}), [licenseId]: value},
                                                    }))}
                                                    isOptionEqualToValue={(option, value) => normalizeReferenceId(option) === normalizeReferenceId(value)}
                                                    getOptionLabel={normalizeRoleLabel}
                                                    sx={autocompleteSx}
                                                    renderInput={(params) => <TextField {...params} placeholder="Escolha os perfis" inputProps={{...params.inputProps, 'aria-label': `Perfis em ${nomeDaLicenca}`}} />}
                                                />
                                            )}
                                            <span className="bf-hint">Os perfis dizem o que a pessoa faz nos sistemas. Você só pode dar perfis com acessos que você já tem.</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </section>
                    </DialogContent>

                    {/* Fora da área que rola: o erro fica à vista de quem acabou de clicar em Criar. */}
                    {data.createError ? <Alert severity="error" sx={{mx: 3, mt: 2}}>{data.createError}</Alert> : null}

                    <div className="bf-dialog__foot">
                        <button type="button" className="bf-button bf-button--ghost" onClick={fecharCadastro} disabled={data.saving}>Cancelar</button>
                        <button type="submit" className="bf-button bf-button--primary" disabled={data.saving}>
                            {data.saving ? <CircularProgress size={16} color="inherit" /> : <UserPlus size={18} />}
                            {data.saving ? 'Criando...' : 'Criar usuário'}
                        </button>
                    </div>
                </form>
            </Dialog>
        </PortalShell>
    );
}
